import React from 'react';
import { AdacLoadingSpinner } from '@adac-de/component-library';
import { useLazyQuery } from '@apollo/client';
import solarApimResultGql from '../../queries/solar-api-result.gql';
import { ConfirmationScreen } from './screen/confirmation-screen';
import { OfferScreen } from './screen/offer-screen';
import { InnerContainer } from '@components/container/inner-container';
import { ContentContainer } from '@components/container/content-container';
import { ErrorMessage } from '@components/error-message/error-message';
import { WizardAction, WizardState } from '@hooks/utils/wizard-types';

type ResultWizardContentProps = {
  headline: PvWizardSectionOffer['headline'];
  benefits: PvWizardSectionOffer['benefits'];
  partners: PvWizardSectionOffer['partners'];
  formHeadlines: PvWizardSectionOffer['formHeadlines'];
  formModals: PvWizardSectionOffer['formModals'];
  selectedPartnersContent: PvWizardSectionOffer['selectedPartnersContent'];
  faqs?: PvWizardSectionOffer['faqs'];
  previousStep: () => void;
  resetWizard: () => void;
  toggleDisclaimerModal: () => void;
  result?: PvWizardResultSectionOffer;
  modalContainerRef: React.RefObject<HTMLDivElement>;
  results?: PvWizardResultSectionOffer['values'];
  scrollIntoView: () => void;
  dispatch: (action: WizardAction) => void;
  wizardState: WizardState;
};

export const ResultWizardContent: React.FC<ResultWizardContentProps> = ({
  headline,
  benefits,
  partners,
  formHeadlines,
  formModals,
  selectedPartnersContent,
  faqs,
  previousStep,
  resetWizard,
  toggleDisclaimerModal,
  result,
  modalContainerRef,
  scrollIntoView,
  dispatch,
  wizardState
}) => {
  const [postSolar, { data: requestResponse, loading, error }] = useLazyQuery<
    SolarApimResultQuery,
    SolarApimResultQueryVariables
  >(solarApimResultGql);

  const hasRequestResponseError =
    requestResponse &&
    (requestResponse.solarApimResult.error || !requestResponse.solarApimResult.result);

  if (!result) {
    return (
      <ContentContainer>
        <ErrorMessage
          headline="Die Ergebnisse der Angebotsseite konnten nicht geladen werden."
          subline="Bitte versuchen Sie ein Browser refresh oder versuchen Sie es später noch mal."
        />
      </ContentContainer>
    );
  }

  if (error || hasRequestResponseError) {
    return (
      <ContentContainer>
        <ErrorMessage
          headline="Beim Versenden der Daten ist ein Fehler aufgetreten."
          subline="Bitte versuchen Sie ein Browser refresh oder versuchen Sie es später noch mal."
        />
      </ContentContainer>
    );
  }

  const submitForm = (data: FormDataType, wizardData: WizardDataType) => {
    postSolar({ variables: { formData: data, wizardData } });
    scrollIntoView();
  };

  return (
    <InnerContainer>
      {!requestResponse && !error && !loading && (
        <OfferScreen
          headline={headline}
          benefits={benefits}
          partners={partners}
          formHeadlines={formHeadlines}
          formModals={formModals}
          selectedPartnersContent={selectedPartnersContent}
          result={result}
          submitForm={submitForm}
          hasVehicle={wizardState.answers.hasVehicle === 'yes'}
          previousStep={previousStep}
          toggleDisclaimerModal={toggleDisclaimerModal}
          modalContainerRef={modalContainerRef}
          dispatch={dispatch}
          partnerOfferingForm={wizardState.partnerOfferingForm}
          scrollIntoView={scrollIntoView}
        />
      )}

      {loading && (
        <AdacLoadingSpinner icon="Lock" className="margin-top--xxxl margin-bottom--xxxl" />
      )}

      {requestResponse && requestResponse.solarApimResult.result && (
        <ConfirmationScreen
          requestResponse={requestResponse.solarApimResult.result}
          resetWizard={resetWizard}
          faqs={faqs}
        />
      )}
    </InnerContainer>
  );
};
